<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type {
  DraftRacePriceTier,
  RacePriceTier,
  StoreRacePriceTierPayload,
} from '@/types/events'
import SlideOver from '@/components/shared/SlideOver.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import PayoutNotReadyNotice from '@/components/organizer/payouts/PayoutNotReadyNotice.vue'
import RacePriceFeePreview from './RacePriceFeePreview.vue'
import { parseRacePriceTierError, useRacePriceTiers } from '@/composables/useRacePriceTiers'
import {
  apiDatetimeToLocalInput,
  isValidTierPrice,
  localInputToApiDatetime,
  newDraftTierKey,
} from '@/utils/racePriceTier'

const props = defineProps<{
  open: boolean
  mode: 'draft' | 'persisted'
  /** null = create a new tier. */
  tier: RacePriceTier | DraftRacePriceTier | null
  organisationId: number
  timezone: string
  eventSlug?: string
  editionSlug?: string
  raceSlug?: string
  /** Draft mode: 422 errors for this row, keyed by field name. */
  externalErrors?: Record<string, string[]>
}>()

const emit = defineEmits<{
  close: []
  saved: [tier: RacePriceTier | DraftRacePriceTier]
}>()

const { t } = useI18n()
const { createTier, updateTier } = useRacePriceTiers(
  props.eventSlug ?? '',
  props.editionSlug ?? '',
  props.raceSlug ?? '',
)

const name = ref('')
const price = ref('')
const endsAt = ref('')
const maxRegistrations = ref('')

const saving = ref(false)
const serverErrors = ref<Record<string, string[]>>({})
const clientErrors = ref<Record<string, string>>({})
const generalError = ref('')
const payoutNotReady = ref<string | null>(null)

const isEditMode = computed<boolean>(() => props.tier !== null)

const showPriceChangeHint = computed<boolean>(
  () => props.tier !== null && 'has_registrations' in props.tier && props.tier.has_registrations,
)

const title = computed<string>(() => (isEditMode.value ? t('races.price_tiers.edit') : t('races.price_tiers.create')))

function resetForm(): void {
  name.value = props.tier?.name ?? ''
  price.value = props.tier?.price ?? ''
  endsAt.value = apiDatetimeToLocalInput(props.tier?.ends_at ?? null)
  maxRegistrations.value = props.tier?.max_registrations != null ? String(props.tier.max_registrations) : ''
  clientErrors.value = {}
  generalError.value = ''
  payoutNotReady.value = null
  serverErrors.value = props.mode === 'draft' ? { ...(props.externalErrors ?? {}) } : {}
}

watch(
  () => props.open,
  (open) => {
    if (open) resetForm()
  },
  { immediate: true },
)

function fieldError(field: string): string | null {
  return clientErrors.value[field] ?? serverErrors.value[field]?.[0] ?? null
}

function validate(): boolean {
  const errors: Record<string, string> = {}
  const trimmed = name.value.trim()
  if (trimmed === '' || trimmed.length > 60) {
    errors['name'] = t('races.price_tiers.form.name_required')
  }
  if (!isValidTierPrice(price.value)) {
    errors['price'] = t('races.price_tiers.form.price_invalid')
  }
  const cap = maxRegistrations.value.trim()
  if (cap !== '' && !/^[1-9]\d*$/.test(cap)) {
    errors['max_registrations'] = t('races.price_tiers.form.max_registrations_invalid')
  }
  if (endsAt.value === '' && cap === '') {
    errors['ends_at'] = t('races.price_tiers.form.bounds_required')
  }
  clientErrors.value = errors
  return Object.keys(errors).length === 0
}

function toPayload(): StoreRacePriceTierPayload {
  const cap = maxRegistrations.value.trim()
  return {
    name: name.value.trim(),
    price: Number(price.value.trim()),
    ends_at: localInputToApiDatetime(endsAt.value),
    max_registrations: cap === '' ? null : parseInt(cap, 10),
  }
}

async function handleSubmit(): Promise<void> {
  if (saving.value) return
  serverErrors.value = {}
  generalError.value = ''
  payoutNotReady.value = null
  if (!validate()) return

  const payload = toPayload()

  if (props.mode === 'draft') {
    const draft: DraftRacePriceTier = {
      key: props.tier !== null && 'key' in props.tier ? props.tier.key : newDraftTierKey(),
      name: payload.name,
      price: price.value.trim(),
      ends_at: payload.ends_at,
      max_registrations: payload.max_registrations,
    }
    emit('saved', draft)
    return
  }

  saving.value = true
  try {
    const saved =
      props.tier !== null && 'id' in props.tier
        ? await updateTier(props.tier.id, payload)
        : await createTier(payload)
    emit('saved', saved)
  } catch (e: unknown) {
    const parsed = parseRacePriceTierError(e)
    if (parsed.payoutNotReady !== null) {
      payoutNotReady.value = parsed.payoutNotReady
    } else if (Object.keys(parsed.errors).length > 0) {
      serverErrors.value = parsed.errors
      // Errors without a field of their own (tiers, tier) are shown as a banner.
      const known = ['name', 'price', 'ends_at', 'max_registrations']
      const other = Object.entries(parsed.errors).find(([key]) => !known.includes(key))
      if (other) generalError.value = other[1][0] ?? t('races.price_tiers.error_generic')
    } else {
      generalError.value = t('races.price_tiers.error_generic')
    }
  } finally {
    saving.value = false
  }
}

function close(): void {
  if (saving.value) return
  emit('close')
}
</script>

<template>
  <SlideOver :open="open" :title="title" :saving="saving" @close="close">
    <form id="race-price-tier-form" novalidate class="space-y-6" @submit.prevent="handleSubmit">
      <PayoutNotReadyNotice v-if="payoutNotReady" :message="payoutNotReady" />

      <div
        v-if="generalError"
        class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
        role="alert"
        data-testid="tier-general-error"
      >
        {{ generalError }}
      </div>

      <!-- Name -->
      <div>
        <label for="tier-name" class="block text-sm font-medium text-slate-300 mb-1">
          {{ t('races.price_tiers.form.name_label') }}
          <span class="text-red-400 ml-0.5">*</span>
        </label>
        <input
          id="tier-name"
          v-model="name"
          type="text"
          maxlength="60"
          :placeholder="t('races.price_tiers.form.name_placeholder')"
          class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          :class="fieldError('name') ? 'border-red-500' : 'border-slate-600'"
        />
        <p v-if="fieldError('name')" class="mt-1 text-xs text-red-400" data-testid="tier-error-name">{{ fieldError('name') }}</p>
      </div>

      <!-- Price (always editable) -->
      <div>
        <label for="tier-price" class="block text-sm font-medium text-slate-300 mb-1">
          {{ t('races.price_tiers.form.price_label') }}
          <span class="text-red-400 ml-0.5">*</span>
        </label>
        <input
          id="tier-price"
          :value="price"
          @input="price = ($event.target as HTMLInputElement).value"
          type="number"
          min="0"
          step="0.01"
          class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          :class="fieldError('price') ? 'border-red-500' : 'border-slate-600'"
        />
        <p v-if="fieldError('price')" class="mt-1 text-xs text-red-400" data-testid="tier-error-price">{{ fieldError('price') }}</p>
        <p v-if="showPriceChangeHint" class="mt-1 text-xs text-amber-400" data-testid="tier-price-change-hint">
          {{ t('races.price_tiers.form.price_change_hint') }}
        </p>
        <div class="mt-3">
          <RacePriceFeePreview :organisation-id="organisationId" :price="price" />
        </div>
      </div>

      <!-- Ends at -->
      <div>
        <label for="tier-ends-at" class="block text-sm font-medium text-slate-300 mb-1">
          {{ t('races.price_tiers.form.ends_at_label') }}
        </label>
        <input
          id="tier-ends-at"
          v-model="endsAt"
          type="datetime-local"
          class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          :class="fieldError('ends_at') ? 'border-red-500' : 'border-slate-600'"
        />
        <p v-if="fieldError('ends_at')" class="mt-1 text-xs text-red-400" data-testid="tier-error-ends-at">{{ fieldError('ends_at') }}</p>
        <p class="mt-1 text-xs text-slate-500">{{ t('races.form.times_in_timezone', { timezone }) }}</p>
      </div>

      <!-- Max registrations -->
      <div>
        <label for="tier-max-registrations" class="block text-sm font-medium text-slate-300 mb-1">
          {{ t('races.price_tiers.form.max_registrations_label') }}
        </label>
        <input
          id="tier-max-registrations"
          :value="maxRegistrations"
          @input="maxRegistrations = ($event.target as HTMLInputElement).value"
          type="number"
          min="1"
          step="1"
          class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          :class="fieldError('max_registrations') ? 'border-red-500' : 'border-slate-600'"
        />
        <p v-if="fieldError('max_registrations')" class="mt-1 text-xs text-red-400" data-testid="tier-error-max-registrations">
          {{ fieldError('max_registrations') }}
        </p>
      </div>

      <p class="text-xs text-slate-500">{{ t('races.price_tiers.form.bounds_hint') }}</p>
    </form>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          :disabled="saving"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
          @click="close"
        >
          {{ t('common.cancel') }}
        </button>
        <button
          type="submit"
          form="race-price-tier-form"
          :disabled="saving"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
          data-testid="tier-submit"
        >
          <AppSpinner v-if="saving" size="button" />
          {{ saving ? t('common.loading') : t('races.price_tiers.form.submit') }}
        </button>
      </div>
    </template>
  </SlideOver>
</template>
